import React, { useEffect, useRef, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

/**
 * Compact option select popover (same idea as Swoop/Communities SelectPopover).
 * options: [{ id, label, icon?, disabled?, note? }, ...]
 */
export const SelectPopover = ({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = options.find((o) => o.id === value) || null;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-theme-secondary px-2.5 py-1 text-xs font-medium text-theme-text hover:bg-theme-secondary/50"
      >
        {selected?.icon}
        <span className="max-w-[10rem] truncate">
          {selected?.label || placeholder}
        </span>
        <ChevronDownIcon
          className={`h-3.5 w-3.5 text-theme-secondary-text transition ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1.5 min-w-[11rem] overflow-hidden rounded-xl border border-theme-secondary bg-theme-main p-1 shadow-xl">
          {options.map((option) => {
            const active = option.id === value;
            return (
              <button
                key={option.id}
                type="button"
                disabled={option.disabled}
                onClick={() => {
                  if (option.disabled) return;
                  onChange(option.id);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm transition ${
                  option.disabled
                    ? "cursor-not-allowed opacity-50"
                    : "seo-row-hover"
                }`}
              >
                <CheckIcon
                  className={`h-4 w-4 shrink-0 ${
                    active ? "text-theme-primary" : "text-transparent"
                  }`}
                />
                {option.icon}
                <span className="min-w-0 flex-1 truncate text-theme-text">
                  {option.label}
                </span>
                {option.note && (
                  <span className="text-[11px] text-theme-secondary-text">{option.note}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SelectPopover;
